"use client";

import { useEffect, useMemo, useState } from "react";

import { useGetCompanyReviewsHeroSummaryQuery } from "@/features/company-reviews/companyReviewInjection";
import { useServiceReviewHeroSummary } from "@/app/services/[...iden]/components/ServiceReviewHeroSummaryContext";
import type { CompanyReviewHeroReviewer } from "@/types/companyReview";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { getDisplayImageUrl } from "@/lib/utils/image-proxy";
import { getCtaDataAttrs } from "@/lib/analytics/cta-attrs";

const REVIEWER_BATCH_SIZE = 5;
const REVIEWER_ROTATE_MS = 10_000;

function SummaryStars({
	rating,
	compact = false,
}: {
	rating: number;
	compact?: boolean;
}) {
	const starClass = compact
		? "inline-flex h-4 w-4 items-center justify-center rounded-none text-[10px] leading-none"
		: "inline-flex h-[1.2rem] w-[1.2rem] items-center justify-center rounded-none text-[11px] leading-none";
	return (
		<div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
			{Array.from({ length: 5 }).map((_, index) => (
				<span
					key={index}
					className={`${starClass} ${
						index < rating ? "bg-tertiary text-white" : "bg-muted text-muted-foreground/50"
					}`}
				>
					★
				</span>
			))}
		</div>
	);
}

function HeroStripSkeleton({
	className,
	compact = false,
}: {
	className?: string;
	compact?: boolean;
}) {
	const avatarSize = compact ? "size-8" : "size-10";
	const starSize = compact ? "size-4" : "size-[1.2rem]";
	const textWidth = compact ? "w-20" : "w-24";
	const gap = compact ? "gap-2" : "gap-3";
	const margin = compact ? "mt-4" : "mt-5";
	return (
		<div
			className={cn(
				margin,
				"mx-auto flex w-fit items-center justify-center",
				gap,
				className,
			)}
			aria-busy="true"
			aria-label="Loading reviews summary"
		>
			<div className="flex items-center">
				{Array.from({ length: 5 }).map((_, index) => (
					<div
						key={index}
						className={cn(
							avatarSize,
							"animate-pulse rounded-full bg-secondary/15 ring-2 ring-tertiary/30",
							index === 0 ? "ml-0" : compact ? "-ml-3" : "-ml-4",
						)}
					/>
				))}
			</div>
			<div className="flex flex-col items-start gap-1.5">
				<div className="flex gap-0.5">
					{Array.from({ length: 5 }).map((_, index) => (
						<div key={index} className={cn(starSize, "animate-pulse bg-secondary/15")} />
					))}
				</div>
				<div className={cn("h-3 animate-pulse rounded bg-secondary/10", textWidth)} />
			</div>
		</div>
	);
}

function shuffleReviewers(reviewers: CompanyReviewHeroReviewer[]) {
	const copy = [...reviewers];
	for (let i = copy.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[copy[i], copy[j]] = [copy[j]!, copy[i]!];
	}
	return copy;
}

function reviewerPhotoUrl(reviewer: CompanyReviewHeroReviewer) {
	return (getDisplayImageUrl(reviewer.avatarUrl) ?? reviewer.avatarUrl)?.trim() || "";
}

function withPhotos(reviewers: CompanyReviewHeroReviewer[]) {
	return reviewers.filter((reviewer) => reviewerPhotoUrl(reviewer).length > 0);
}

function useImagesReady(urls: string[]) {
	const key = urls.join("|");
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (urls.length === 0) {
			setReady(false);
			return;
		}
		let cancelled = false;
		let remaining = urls.length;
		setReady(false);

		const finish = () => {
			remaining -= 1;
			if (!cancelled && remaining <= 0) setReady(true);
		};

		const images = urls.map((url) => {
			const img = new window.Image();
			img.onload = finish;
			img.onerror = finish;
			img.src = url;
			if (img.complete) {
				img.onload = null;
				img.onerror = null;
				finish();
			}
			return img;
		});

		return () => {
			cancelled = true;
			for (const img of images) {
				img.onload = null;
				img.onerror = null;
			}
		};
	}, [key]);

	return ready;
}

type ServiceReviewSummaryStripProps = {
	/** Merged onto the root control; use e.g. `lg:mx-0 lg:justify-start` when the parent is left-aligned. */
	className?: string;
	/** Hero CTAs strip only — smaller than About section copy. */
	variant?: "default" | "compact";
	/** Unique GA4 location for this strip instance. */
	ctaLocation?: string;
};

export default function ServiceReviewSummaryStrip({
	className,
	variant = "default",
	ctaLocation = "service_review_summary",
}: ServiceReviewSummaryStripProps = {}) {
	const compact = variant === "compact";
	const ctaId =
		ctaLocation === "service_hero_review_summary"
			? "review_summary_jump_hero"
			: ctaLocation === "service_about_review_summary"
				? "review_summary_jump_about"
				: ctaLocation === "service_post_guarantee_review_summary"
					? "review_summary_jump_post_guarantee"
					: "review_summary_jump";
	const initialSummary = useServiceReviewHeroSummary();
	const skipFetch = initialSummary != null;
	const { data: fetchedSummary, isLoading } = useGetCompanyReviewsHeroSummaryQuery(undefined, {
		skip: skipFetch,
	});
	const summary = initialSummary ?? fetchedSummary;
	const baseReviewers = withPhotos(summary?.reviewers ?? []);
	const reviewersKey = baseReviewers.map((r) => r.reviewId).join("|");

	/** Stable API order for SSR + hydration; shuffle only after mount (Math.random must not run on server). */
	const [displayReviewers, setDisplayReviewers] =
		useState<CompanyReviewHeroReviewer[]>(baseReviewers);

	useEffect(() => {
		const reviewers = withPhotos(summary?.reviewers ?? []);
		setDisplayReviewers(reviewers);
		setBatchIndex(0);
	}, [reviewersKey, summary]);

	useEffect(() => {
		const reviewers = withPhotos(summary?.reviewers ?? []);
		if (reviewers.length < 2) return;
		setDisplayReviewers(shuffleReviewers([...reviewers]));
	}, [reviewersKey, summary]);

	const reviewerBatches = useMemo(() => {
		if (displayReviewers.length === 0) return [];
		const batches: CompanyReviewHeroReviewer[][] = [];
		for (let i = 0; i < displayReviewers.length; i += REVIEWER_BATCH_SIZE) {
			batches.push(displayReviewers.slice(i, i + REVIEWER_BATCH_SIZE));
		}
		return batches;
	}, [displayReviewers]);

	const [batchIndex, setBatchIndex] = useState(0);

	const visibleReviewers = useMemo(() => {
		const batch = reviewerBatches[batchIndex] ?? [];
		if (batch.length >= REVIEWER_BATCH_SIZE) return batch;
		if (displayReviewers.length === 0) return [];

		const filled = [...batch];
		let i = 0;
		while (filled.length < REVIEWER_BATCH_SIZE) {
			filled.push(displayReviewers[i % displayReviewers.length]!);
			i += 1;
		}
		return filled;
	}, [batchIndex, reviewerBatches, displayReviewers]);

	const visiblePhotoUrls = useMemo(
		() => visibleReviewers.map((reviewer) => reviewerPhotoUrl(reviewer)),
		[visibleReviewers],
	);
	const batchPhotosReady = useImagesReady(visiblePhotoUrls);
	const [paintedReviewers, setPaintedReviewers] = useState<CompanyReviewHeroReviewer[]>([]);

	useEffect(() => {
		if (!batchPhotosReady || visibleReviewers.length === 0) return;
		setPaintedReviewers(visibleReviewers);
	}, [batchPhotosReady, visibleReviewers]);

	useEffect(() => {
		if (displayReviewers.length === 0) return;
		for (const reviewer of displayReviewers) {
			const url = reviewerPhotoUrl(reviewer);
			if (!url) continue;
			const img = new window.Image();
			img.src = url;
		}
	}, [reviewersKey, displayReviewers]);

	const averageRating = summary?.averageRating ?? 0;
	const totalReviews = summary?.totalDocs ?? 0;

	useEffect(() => {
		setBatchIndex(0);
	}, [reviewerBatches.length]);

	useEffect(() => {
		if (reviewerBatches.length <= 1) return;
		const timer = window.setInterval(() => {
			setBatchIndex((prev) => (prev + 1) % reviewerBatches.length);
		}, REVIEWER_ROTATE_MS);
		return () => window.clearInterval(timer);
	}, [reviewerBatches.length]);

	if (!skipFetch && isLoading) {
		return <HeroStripSkeleton className={className} compact={compact} />;
	}

	if (visibleReviewers.length === 0) {
		return (
			<div
				className={cn(
					compact ? "mt-4 h-8" : "mt-5 h-10",
					"mx-auto flex w-[15rem] max-w-full items-center justify-center opacity-0",
					compact && "w-[12rem]",
					className,
				)}
				aria-hidden
			/>
		);
	}

	if (paintedReviewers.length === 0) {
		return <HeroStripSkeleton className={className} compact={compact} />;
	}

	const scrollToReviews = () => {
		const target = document.getElementById("service-reviews-section");
		if (!target) return;
		target.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	return (
		<button
			type="button"
			onClick={scrollToReviews}
			{...getCtaDataAttrs({
				ctaId,
				ctaName: "Jump to customer reviews",
				ctaLocation,
			})}
			className={cn(
				compact ? "mt-4 gap-2" : "mt-5 gap-3",
				"mx-auto flex w-fit cursor-pointer items-center justify-center",
				className,
			)}
			aria-label="Jump to customer reviews"
		>
			<div className="flex items-center">
				{paintedReviewers.map((reviewer, index) => {
					const avatarUrl = reviewerPhotoUrl(reviewer);
					return (
						<div
							key={reviewer.reviewId}
							style={{ zIndex: index + 1 }}
							className={cn(
								"relative inline-flex items-center justify-center overflow-hidden rounded-full bg-background ring-2 ring-tertiary",
								compact ? "h-8 w-8 ring-[1.5px]" : "h-10 w-10",
								index === 0 ? "ml-0" : compact ? "-ml-3" : "-ml-4",
							)}
							title="Reviewer"
						>
							<Avatar className="h-full w-full rounded-full">
								<AvatarImage
									src={avatarUrl}
									alt=""
									className="object-cover"
									loading="eager"
									decoding="async"
								/>
							</Avatar>
						</div>
					);
				})}
			</div>

			<div className="flex flex-col items-start">
				<SummaryStars
					rating={Math.min(5, Math.max(1, Math.round(averageRating)))}
					compact={compact}
				/>
				<p
					className={cn(
						"mt-0.5 leading-none text-foreground",
						compact ? "text-[11px]" : "text-xs",
					)}
				>
					<span className="font-medium">{averageRating.toFixed(1)}</span>
					<span aria-hidden className="mx-1 text-foreground/50">
						·
					</span>
					<span className="text-muted-foreground">{totalReviews.toLocaleString("en-GB")} reviews</span>
				</p>
			</div>
		</button>
	);
}
